import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChallengeInstance } from '../../../hooks/useChallengeInstance';
import { useEvaluateStep } from '../../../hooks/useEvaluateStep';
import LoadingAnimation from "../../../common/components/base/LoadingAnimation";

// Wrapper component to use hooks
const ChallengeInstanceHookWrapper = ({ WrappedComponent, action, skip, skipSteps, ...props }) => {
  const { id: challengeId } = useParams();
  
  const navigate = useNavigate();
  const { data, isFetching: isLoading, error } = useChallengeInstance(challengeId);
  const evaluateStepMutation = useEvaluateStep();
  
  const [stepTime, setStepTime] = useState(0);

  // Start step timer
  useEffect(() => {
    const timer = setInterval(() => {
      setStepTime(prevTime => prevTime + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  if (isLoading) return <LoadingAnimation />;
  if (error) return <div>Error loading challenge: {error.message}</div>;
  if (!data?.challengeInstance?.challengeType) return <LoadingAnimation />;

  return (
    <ChallengeInstanceClass 
      WrappedComponent={WrappedComponent}
      action={action}
      skip={skip}
      skipSteps={skipSteps}
      challengeType={data?.challengeInstance?.challengeType}
      currentStep={data?.challengeInstance?.currentStep}
      evaluateStepMutation={evaluateStepMutation}
      navigate={navigate}
      challengeId={challengeId}
      stepTime={stepTime}
      {...props}
    />
  );
};

class ChallengeInstanceClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      id: props.challengeId, 
      time: 120,
      type: props.challengeType,
      currentStep: props.currentStep
    };
    this._add = this._add.bind(this)
    this._skip = this._skip.bind(this)
  }

  async _add() {
    const {id} = this.state;
    try {
      const actionWithTime = {
        ...this.props.action,
        stepTime: this.props.stepTime
      };
      const res = await this.props.evaluateStepMutation.mutateAsync({
        challengeId: id,
        action: actionWithTime,
        challengeToDo: null
      });
      let value = res.challengeStepEvaluator?.targetLocation || '';
      if (value !== "")
        this.props.navigate(`${value}${id}`);
    } catch (error) {
      console.error('Error evaluating step:', error);
    }
  }
  async _skip() {
    const {id} = this.state;
    try {
      for (let i = 0; i < this.props.skipSteps; i++) {
        const skipActionWithTime = {
          ...this.props.skip,
          stepTime: this.props.stepTime
        };
        const res = await this.props.evaluateStepMutation.mutateAsync({
          challengeId: id,
          action: skipActionWithTime,
          challengeToDo: null
        });
        let value = res.challengeStepEvaluator?.targetLocation || '';
        if (value !== "")
          this.props.navigate(`${value}${id}`);
      }
    } catch (error) {
      console.error('Error skipping steps:', error);
    }
  }

  // TODO: Replaced with REST hook wrapper
  // componentDidMount() {
  //   client
  //     .query({
  //       query: GET_CHALLENGE_INSTANCE,
  //       variables: { id: this.state.id },
  //       fetchPolicy: 'no-cache'
  //     })
  //     .then(res => {
  //       this.setState({
  //         type: res.data.challengeInstance.challengeType,
  //         currentStep: res.data.challengeInstance.currentStep
  //         
  //       });
  //     });
  // }

  render() {
    const { type } = this.state;
    if (!type) return <LoadingAnimation />;

    return (
      <this.props.WrappedComponent _add={this._add} _skip={this._skip} type={type} {...this.props} />
    );
  }
}

function withChallengeInstance(WrappedComponent, action, skip, skipSteps) {
  return (props) => (
    <ChallengeInstanceHookWrapper
      WrappedComponent={WrappedComponent}
      action={action}
      skip={skip}
      skipSteps={skipSteps}
      {...props}
    />
  );
}

export default withChallengeInstance;
